import { useState } from "react";
import { fmtCompact, fmtRub } from "../../lib/format";
import type { PnlEvent } from "./useStatsHistory";

// Кривая накопленного реализованного P&L. События приходят с бэка уже
// отсортированными по дате, с готовым `cumulative_rub` — рисуем линию по нему,
// точки красим по знаку вклада события (`realised_rub`).
export function EquityCurve({ events }: { events: PnlEvent[] }) {
  const [hi, setHi] = useState<number | null>(null);
  const w = 720;
  const h = 220;
  const pad = 34;

  const vals = events.map((e) => e.cumulative_rub);
  const n = events.length;
  const xs = events.map((_, i) =>
    n === 1 ? w / 2 : pad + (i / (n - 1)) * (w - pad * 2),
  );
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 0);
  const span = max - min || 1;
  const ys = vals.map((v) => h - pad - ((v - min) / span) * (h - pad * 2));

  const points = xs.map((x, i) => `${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const area = `${xs[0]},${h - pad} ${points} ${xs[n - 1]},${h - pad}`;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ display: "block" }}>
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const y = pad + t * (h - pad * 2);
          const v = max - t * span;
          return (
            <g key={i}>
              <line
                x1={pad}
                x2={w - pad}
                y1={y}
                y2={y}
                stroke="var(--line)"
                strokeDasharray="2 3"
              />
              <text
                x={pad - 6}
                y={y + 3}
                fontSize="10"
                fill="var(--ink-soft)"
                textAnchor="end"
              >
                {fmtCompact(v)}
              </text>
            </g>
          );
        })}
        <polygon points={area} fill="var(--accent)" opacity="0.08" />
        <polyline points={points} fill="none" stroke="var(--accent)" strokeWidth="1.8" />
        {events.map((e, i) => (
          <circle
            key={i}
            cx={xs[i]}
            cy={ys[i]}
            r={hi === i ? 4.5 : 2.5}
            fill={e.realised_rub >= 0 ? "var(--good)" : "var(--bad)"}
            style={{ cursor: "pointer", transition: "r 0.12s" }}
            onMouseEnter={() => setHi(i)}
            onMouseLeave={() => setHi(null)}
          />
        ))}
        {events.map((e, i) =>
          n > 1 && i % Math.ceil(n / 6) === 0 ? (
            <text
              key={`x${i}`}
              x={xs[i]}
              y={h - pad + 16}
              fontSize="10"
              fill="var(--ink-soft)"
              textAnchor="middle"
            >
              {e.date.slice(5, 7)}.{e.date.slice(2, 4)}
            </text>
          ) : null,
        )}
      </svg>
      {hi !== null && (
        <div
          style={{
            position: "absolute",
            left: `calc(${(xs[hi] / w) * 100}% - 80px)`,
            top: `${(ys[hi] / h) * 100}%`,
            marginTop: -78,
            width: 172,
            background: "var(--ink)",
            color: "#fff",
            borderRadius: 6,
            padding: "8px 11px",
            fontSize: 12,
            pointerEvents: "none",
            boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
          }}
        >
          <div style={{ opacity: 0.7, fontSize: 10.5, letterSpacing: "0.05em" }}>
            {events[hi].date}
          </div>
          <div style={{ marginTop: 2 }}>{events[hi].name}</div>
          <div
            className="mono"
            style={{
              fontSize: 12.5,
              marginTop: 4,
              color: events[hi].realised_rub >= 0 ? "#7ee7a9" : "#f3a98e",
            }}
          >
            {events[hi].realised_rub >= 0 ? "+" : ""}
            {fmtRub(events[hi].realised_rub)}
          </div>
          <div style={{ fontSize: 11, marginTop: 2, opacity: 0.7 }}>
            накоплено: {fmtRub(events[hi].cumulative_rub)}
          </div>
        </div>
      )}
    </div>
  );
}
